<template>
  <v-container id="alerts" fluid tag="section" class="news-page pa-4 pa-md-6">
    <v-card flat class="page-header-card mb-4">
      <v-card-text class="d-flex align-center pa-4">
        <div class="page-header-icon mr-3">
          <v-icon size="24" color="white">mdi-bullhorn-outline</v-icon>
        </div>
        <div>
          <div class="page-header-title">ข่าวประชาสัมพันธ์ และคู่มือการใช้งาน</div>
          <div class="page-header-subtitle grey--text text--darken-1">
            ข่าวสาร ประกาศ เกณฑ์ต่างๆ พร้อมคู่มือการใช้งานระบบ
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-row>
      <!-- =============================== -->
      <!-- ส่วนข่าวสารประชาสัมพันธ์ -->
      <!-- =============================== -->
      <v-col cols="12" md="8">
        <v-card class="section-card">
          <div class="section-card-header">
            <v-icon size="18" color="white" class="mr-2">mdi-bullhorn-outline</v-icon>
            <span>ประชาสัมพันธ์</span>
            <v-spacer />
            <v-chip x-small outlined color="white" text-color="white">
              {{ news_ss.length }} รายการ
            </v-chip>
          </div>
          <v-card-text>
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="ค้นหา ระบุคำ หรือ ส่วนข้อความเกี่ยวข้อง"
              single-line
              hide-details
              dense
              filled
              rounded
              clearable
              class="mb-4"
              style="max-width: 400px;"
            />

            <v-data-table
              :loading="loading"
              loading-text="กำลังโหลดข้อมูล..."
              :headers="headers"
              :items="news_ss"
              :search="search"
              :items-per-page="10"
              :footer-props="{
                'items-per-page-options': [10, 25, 50],
                'items-per-page-text': 'แสดงต่อหน้า',
              }"
              class="elevation-0"
            >
              <!-- หัวข้อ -->
              <template v-slot:item.topic_s="{ item }">
                <span class="font-weight-bold">{{ item.topic_s }}</span>
              </template>

              <!-- วันที่ -->
              <template v-slot:item.date_times="{ item }">
                {{ formatDateThai(item.date_times) }}
              </template>

              <!-- ไฟล์แนบ: แสดงภาพปก PDF -->
              <template v-slot:item.link_file="{ item }">
                <div
                  v-if="item.link_file"
                  class="pdf-thumbnail-wrap my-2"
                  @click="openPdfDialog(item)"
                  style="cursor: pointer;"
                >
                  <canvas
                    :ref="'thumb_news_' + item.id_new"
                    class="pdf-thumbnail elevation-2"
                  ></canvas>
                  <div v-if="!thumbOk['thumb_news_' + item.id_new]" class="thumb-fallback">
                    <v-icon color="grey darken-1" size="26">mdi-file-pdf-box</v-icon>
                    <span class="thumb-fallback-text">ดูไฟล์แนบ</span>
                  </div>
                  <div class="pdf-thumbnail-overlay">
                    <v-icon color="white" small>mdi-eye</v-icon>
                  </div>
                </div>
                <span v-else class="grey--text text-caption">ไม่มีไฟล์</span>
              </template>

              <template v-slot:no-results>
                <v-alert type="warning" text class="ma-2">
                  ไม่พบผลลัพธ์ "{{ search }}" ที่คุณกำลังค้นหา
                </v-alert>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- =============================== -->
      <!-- ส่วนคู่มือการใช้งาน -->
      <!-- =============================== -->
      <v-col cols="12" md="4">
        <v-card class="section-card">
          <div class="section-card-header">
            <v-icon size="18" color="white" class="mr-2">mdi-book-open-page-variant-outline</v-icon>
            <span>คู่มือการใช้งาน</span>
            <v-spacer />
            <v-chip x-small outlined color="white" text-color="white">
              {{ manual_ss.length }} รายการ
            </v-chip>
          </div>
          <v-card-text>
            <v-card
              v-for="item in manual_ss"
              :key="item.id_manual"
              class="mb-4 manual-card"
              outlined
              hover
              @click="openPdfDialog(item, 'manual')"
              style="cursor: pointer;"
            >
              <!-- ภาพปกหน้าแรก PDF -->
              <div class="manual-cover-wrap">
                <canvas
                  :ref="'thumb_manual_' + item.id_manual"
                  class="manual-cover"
                ></canvas>
                <div v-if="!thumbOk['thumb_manual_' + item.id_manual]" class="thumb-fallback thumb-fallback--lg">
                  <v-icon color="grey darken-1" size="44">mdi-file-pdf-box</v-icon>
                  <span class="thumb-fallback-text">คู่มือ PDF</span>
                </div>
                <div class="manual-cover-overlay">
                  <v-icon color="white" size="36">mdi-file-eye-outline</v-icon>
                  <div class="white--text mt-1 text-caption">คลิกเพื่อเปิดดู</div>
                </div>
              </div>

              <v-card-title class="subtitle-1 font-weight-bold pb-1">
                {{ item.topic_s }}
              </v-card-title>

              <v-card-subtitle class="pb-1">
                {{ item.detail_s }}
              </v-card-subtitle>

              <v-card-actions class="pt-0">
                <v-chip x-small color="#263238" text-color="white">
                  <v-icon x-small left>mdi-calendar</v-icon>
                  {{ formatDateThai(item.date_time) }}
                </v-chip>
                <v-spacer />
                <v-btn x-small text color="#263238" @click.stop="openPdfDialog(item, 'manual')">
                  <v-icon x-small left>mdi-book-open-variant</v-icon>
                  เปิดคู่มือ
                </v-btn>
              </v-card-actions>
            </v-card>

            <v-alert v-if="manual_ss.length === 0 && !loading" type="info" text dense>
              ยังไม่มีคู่มือการใช้งาน
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- =============================== -->
    <!-- Dialog แสดง PDF -->
    <!-- =============================== -->
    <v-dialog v-model="pdfDialog" max-width="950" scrollable>
      <v-card>
        <v-toolbar flat color="#263238" dark dense>
          <v-icon left>mdi-file-pdf-box</v-icon>
          <v-toolbar-title>{{ pdfDialogTitle }}</v-toolbar-title>
          <v-spacer />
          <v-btn icon :href="pdfDialogUrl" target="_blank" title="เปิดในแท็บใหม่">
            <v-icon>mdi-open-in-new</v-icon>
          </v-btn>
          <v-btn icon :href="pdfDialogUrl" download title="ดาวน์โหลด">
            <v-icon>mdi-download</v-icon>
          </v-btn>
          <v-btn icon @click="pdfDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-0" style="height: 80vh;">
          <iframe
            v-if="pdfDialogUrl"
            :src="pdfDialogUrl"
            width="100%"
            height="100%"
            frameborder="0"
            style="border: none;"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
/* 
  ใช้ PDF.js จาก CDN เพื่อ render หน้าแรกของ PDF เป็นภาพปก
  เพิ่มใน index.html:
  <script src="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js"><\/script>

  หรือ install ผ่าน npm:
  npm install pdfjs-dist@2.16.105
  แล้ว import ด้านล่าง
*/

// ถ้าใช้ CDN ให้ comment บรรทัดนี้ออก
// import * as pdfjsLib from 'pdfjs-dist'

export default {
  name: "DashboardNotifications",

  data: () => ({
    ApiKey: "HRvec2021",
    fileBasePath: "/HRvecfiles/",
    loading: false,
    search: "",

    news_ss: [],
    manual_ss: [],

    headers: [
      { text: "หัวข้อ", align: "left", value: "topic_s" },
      { text: "รายละเอียด", align: "left", value: "detail_s" },
      { text: "วันที่", align: "center", value: "date_times", width: "150" },
      { text: "ไฟล์แนบ", align: "center", value: "link_file", sortable: false, width: "120" },
    ],

    // PDF Dialog
    pdfDialog: false,
    pdfDialogTitle: "",
    pdfDialogUrl: "",

    // สถานะการ render thumbnail ต่อไฟล์ (refName -> true เมื่อวาดสำเร็จ)
    // ใช้แสดง fallback icon แทนช่องว่างเปล่าตอนที่ยังไม่ render เสร็จ/ล้มเหลว
    thumbOk: {},
  }),

  async mounted() {
    // ตั้งค่า PDF.js worker
    this.initPdfJs();

    await Promise.all([
      this.fetchNews(),
      this.fetchManuals(),
    ]);

    // render thumbnail หลังข้อมูลโหลดเสร็จ
    this.$nextTick(() => {
      this.renderAllThumbnails();
    });
  },

  methods: {
    // ============ PDF.js Setup ============

    initPdfJs() {
      // ถ้าใช้ CDN
      if (window.pdfjsLib) {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js";
      }
    },

    getPdfJs() {
      // ใช้ CDN global หรือ import
      return window.pdfjsLib || null;
    },

    // ============ ดึงข้อมูล ============

    async fetchNews() {
      this.loading = true;
      try {
        const result = await this.$http.post("news_s.php", {
          ApiKey: this.ApiKey,
        });
        this.news_ss = result.data;
      } catch (error) {
        console.error("โหลดข่าวสารผิดพลาด:", error);
      } finally {
        this.loading = false;
      }
    },

    async fetchManuals() {
      try {
        const result = await this.$http.post("manual_s.php", {
          ApiKey: this.ApiKey,
        });
        this.manual_ss = result.data;
      } catch (error) {
        console.error("โหลดคู่มือผิดพลาด:", error);
      }
    },

    // ============ Render Thumbnails ============

    renderAllThumbnails() {
      // render ปกข่าวสาร
      this.news_ss.forEach((item) => {
        if (item.link_file) {
          const refName = "thumb_news_" + item.id_new;
          this.renderPdfThumbnail(
            this.fileBasePath + item.link_file,
            refName,
            100 // ความสูง thumbnail สำหรับตาราง
          );
        }
      });

      // render ปกคู่มือ
      this.manual_ss.forEach((item) => {
        if (item.link_file) {
          const refName = "thumb_manual_" + item.id_manual;
          this.renderPdfThumbnail(
            this.fileBasePath + item.link_file,
            refName,
            200 // ความสูง thumbnail สำหรับ card
          );
        }
      });
    },

    async renderPdfThumbnail(pdfUrl, refName, desiredHeight) {
      const pdfjsLib = this.getPdfJs();
      if (!pdfjsLib) {
        console.warn("PDF.js ยังไม่ได้โหลด กรุณาเพิ่ม PDF.js CDN ใน index.html");
        this.$set(this.thumbOk, refName, false);
        return;
      }

      try {
        const canvasEl = this.$refs[refName];
        const canvas = Array.isArray(canvasEl) ? canvasEl[0] : canvasEl;
        if (!canvas) {
          this.$set(this.thumbOk, refName, false);
          return;
        }

        const pdf = await pdfjsLib.getDocument(pdfUrl).promise;
        const page = await pdf.getPage(1);

        // คำนวณ scale ให้ได้ความสูงตามต้องการ
        const originalViewport = page.getViewport({ scale: 1 });
        const scale = desiredHeight / originalViewport.height;
        const viewport = page.getViewport({ scale });

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const ctx = canvas.getContext("2d");
        await page.render({
          canvasContext: ctx,
          viewport: viewport,
        }).promise;

        // วาดสำเร็จ ซ่อน fallback icon แล้วแสดง thumbnail จริง
        this.$set(this.thumbOk, refName, true);
      } catch (error) {
        console.warn("ไม่สามารถ render thumbnail:", pdfUrl, error);
        this.$set(this.thumbOk, refName, false);
      }
    },

    // ============ PDF Dialog ============

    openPdfDialog(item, type) {
      const fileName = item.link_file;
      if (!fileName) return;

      this.pdfDialogTitle = item.topic_s || "เอกสาร";
      this.pdfDialogUrl = this.fileBasePath + fileName;
      this.pdfDialog = true;
    },

    // ============ Utility ============

    formatDateThai(dateStr) {
      if (!dateStr) return "-";
      try {
        const date = new Date(dateStr);
        // แปลงเป็น พ.ศ.
        const options = { year: "numeric", month: "long", day: "numeric" };
        const formatted = date.toLocaleDateString("th-TH", options);
        return formatted;
      } catch {
        return dateStr;
      }
    },
  },
};
</script>

<style scoped>
/* =============================== */
/* Page header (มาตรฐานเดียวกับหน้าอื่น) */
/* =============================== */
.news-page {
  max-width: 1400px;
  margin: 0 auto;
}

.page-header-card {
  border-radius: 14px !important;
  border: 1px solid #e4e4e7 !important;
  background: #ffffff !important;
}

.page-header-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, #263238, #263238);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.page-header-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #263238;
  line-height: 1.3;
}

.page-header-subtitle {
  font-size: 0.82rem;
  margin-top: 2px;
}

/* Section card (การ์ดข่าว/คู่มือ) */
.section-card {
  border-radius: 14px !important;
  border: 1px solid #e4e4e7 !important;
  overflow: hidden;
}

.section-card-header {
  display: flex;
  align-items: center;
  background: #263238;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 700;
  padding: 12px 20px;
}

/* =============================== */
/* Thumbnail สำหรับตารางข่าวสาร     */
/* =============================== */
.pdf-thumbnail-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 76px;
  height: 100px;
  border-radius: 4px;
  overflow: hidden;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.pdf-thumbnail-wrap:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.pdf-thumbnail {
  display: block;
  border-radius: 4px;
  max-width: 100%;
  max-height: 100px;
}

/* Fallback icon ที่แสดงเสมอจนกว่าจะ render thumbnail จริงสำเร็จ
   กันไม่ให้ผู้ใช้เห็นเป็นช่องว่างเปล่าที่ต้อง hover ถึงจะสังเกตเห็น */
.thumb-fallback {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  background: #f5f5f5;
}

.thumb-fallback-text {
  font-size: 0.6rem;
  color: #757575;
  font-weight: 600;
}

.thumb-fallback--lg .thumb-fallback-text {
  font-size: 0.75rem;
}

.pdf-thumbnail-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  border-radius: 4px;
}

.pdf-thumbnail-wrap:hover .pdf-thumbnail-overlay {
  opacity: 1;
}

/* =============================== */
/* ภาพปกสำหรับ Card คู่มือ          */
/* =============================== */
.manual-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;
}

.manual-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12) !important;
}

.manual-cover-wrap {
  position: relative;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  overflow: hidden;
}

.manual-cover {
  display: block;
  width: 100%;
  object-fit: contain;
}

.manual-cover-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.25s ease;
}

.manual-cover-wrap:hover .manual-cover-overlay {
  opacity: 1;
}
</style>